import { intl } from "LocaleWrapper";
import { z } from "zod";
import { ZodSchema } from "@validation/utils/schemaUtils";

import { locationSelectOptionSchema } from "view/locations/utils/location.schema";
import { containerTypeSelectOptionSchema } from "view/containertypes/utils/containerType.shema";
import { relationSelectOptionSchema } from "view/relations/utils/relation.schema";
import { productSelectOptionSchema } from "view/products/utils/product.schema";

import { CallType, VolumeUnit, WeightUnit } from "__generated__/enums";

export type CargoActualsInput = z.input<typeof CargoActualsSchema>;
export type CargoActualsOutput = z.output<typeof CargoActualsSchema>;

export const ExpectedCargoSchema = z.object({
	id: z.number().nullable(),
	actionType: z.enum(CallType),
	dischargingLocation: locationSelectOptionSchema.nullable(),
	amount: ZodSchema.requiredField(z.int32()),
	containerType: ZodSchema.requiredField(containerTypeSelectOptionSchema),
	full: z.boolean(),
}).refine(expectedCargo => expectedCargo.actionType !== CallType.Loading || expectedCargo.dischargingLocation, {
	error: intl.formatMessage({ id: "REQUIRED" }),
	path: [ "dischargingLocation" ],
	when(payload) {
		return ExpectedCargoSchema
			.pick({ actionType: true, dischargingLocation: true })
			.safeParse(payload.value).success;
	},
});

export const CargoActualsSchema = z.object({
	id: z.number(),
	weight: z.number().nullable(),
	volume: z.number().nullable(),
	measurements: z.object({
		locationVolume: z.float64().max(9999999.999).nullable(),
		locationVolumeUnit: z.enum(VolumeUnit).nullable(),
		skipperVolume: z.float64().max(9999999.999).nullable(),
		skipperVolumeUnit: z.enum(VolumeUnit).nullable(),
		locationWeight: z.float64().max(9999999.999).nullable(),
		locationWeightUnit: z.enum(WeightUnit).nullable(),
		skipperWeight: z.float64().max(9999999.999).nullable(),
		skipperWeightUnit: z.enum(WeightUnit).nullable(),
		relativeDensity: z.float64().max(999999.9999).nullable(),
		productTemperature: z.float64().max(99999.999).nullable(),
		ambientVolume: z.float64().max(999999999.999).nullable(),
		actualConsignor: z.string()
			.transform(value => value.trim())
			.transform(value => value === "" ? null : value),
		actualConsignee: z.string()
			.transform(value => value.trim())
			.transform(value => value === "" ? null : value),
		protest: z.boolean(),
	}),
});

export const updateCargoFieldsSchema = z.object({
	id: z.number(),
	loadingReference: z.string().max(50),
	dischargingReference: z.string().max(50),
	supplier: relationSelectOptionSchema.nullable(),
	product: ZodSchema.requiredField(productSelectOptionSchema),
	actions: z.array(z.object({
		id: z.number(),
		type: z.enum(CallType),
		location: ZodSchema.requiredField(locationSelectOptionSchema),
		weight: z.number().min(0).max(9999).nullable(),
		volume: z.number().min(0).max(99999).nullable(),
	})),
});

export type UpdateCargoFieldsSchemaInput = z.input<typeof updateCargoFieldsSchema>;
export type UpdateCargoFieldsSchemaOutput = z.output<typeof updateCargoFieldsSchema>;
